import React from 'react';
import moment from 'moment-timezone';
import './subscriptionSummaryListView.module.scss';
import { DetailedDate2, DetailedDateRange, DetailedPeriod } from '../../../../../admin/components/date/TimeRange';
import { MobBr, SpBr } from '../../../../../commonv3/components/atoms/layouts/br';
import { Button as OldButton } from '../../../../../commonv3/components/atoms/buttons/Button';
import { MembershipItemId } from '../../../../../commonv3/types/membershipPlans';
import { GlobalStatesV3 } from '../../../../../commonv3/states/GlobalStatesV3';
import { V3Routes } from '../../../../routes';
import { SubscriptionSummary } from '../../../../types/subscriptionSummary';
import { usePlanOperationConfig } from '../../../../../commonv3/hooks/usePlanOperationConfig';
import { Warning } from '../../../atoms/membership_plans/Warning';
import { Alert, AlertContent, Button } from '@sheinc/shelikes-design-system';

const SubscriptionSummaryRow = ({ title, children, className = undefined }) => (
  <div styleName="subscription-summary-row" className={className}>
    <div styleName="title">{title}</div>
    <div styleName="body">{children}</div>
  </div>
);

type Props = {
  summary: SubscriptionSummary;
  loading?: boolean;
  isWithdrawalSet: boolean;
  onUpdateRequest?: (itemId: MembershipItemId) => void;
  onUpdateCancelRequest?: (originalItemId: MembershipItemId, nextItemId: MembershipItemId) => void;
  onCancelSuspensionRequest?: (subscriptionCode: string, subscriptionSuspensionCode: string) => Promise<void>;
  onResumeSubscriptionRequest?: (subscriptionCode: string) => void;
};

export const SubscriptionSummaryView = ({
  summary,
  isWithdrawalSet,
  onUpdateRequest,
  onUpdateCancelRequest,
  onCancelSuspensionRequest,
  onResumeSubscriptionRequest,
}: Props) => {
  const {
    subscribed,
    next_period_membership_item,
    next_period_membership_item_id,
    current_period_membership_item_id,
    suspended,
    future_suspension,
    subscription_suspension,
    next_payment_date,
    plan_item_price,
    chain_id,
    plan_name,
    next_payment_amount,
    starts_at,
    strict_expires_at,
    subscription_code,
    current_period_starts_at,
    next_period_starts_at,
  } = summary;
  const config = usePlanOperationConfig();
  // プラン開始前だったら true、プランが開始していたら false
  const future = moment(starts_at).isAfter(moment());
  // プラン変更を予約中かどうか
  const hasFutureChangePlan =
    next_period_membership_item != null && next_period_membership_item_id != current_period_membership_item_id;
  // 休会中かどうか
  const isSuspended = subscribed && suspended;
  // 通常休会
  const isRecess = isSuspended && subscription_suspension.intent === 'recess';
  // 支払い待ち
  const isWaitingForPayment = isSuspended && subscription_suspension.intent === 'waiting_for_payment';
  // 支払い完了
  const isPaymentCompleted = isSuspended && subscription_suspension.intent === 'payment_completed';
  // 休会予約が存在しているかどうか
  const hasFutureSuspension = subscribed && subscription_suspension && future_suspension;
  // 退会、休会、プラン変更の予約が存在せず、プラン変更または休会手続きが可能かどうか
  const canChangePlanOrSuspendPlan =
    subscribed &&
    !isSuspended &&
    !isWithdrawalSet &&
    !hasFutureChangePlan &&
    !hasFutureSuspension &&
    config.planUpdateEnabled;
  // 次回の支払い予定があるかどうか
  const hasNextPayment =
    subscribed &&
    !isWithdrawalSet &&
    !hasFutureChangePlan &&
    !hasFutureSuspension &&
    next_payment_date &&
    plan_item_price > 0;

  const isSp = GlobalStatesV3.viewport.isSp;
  return (
    <>
      <div
        styleName="subscription-summary"
        className="subscription-summary-view"
        id={`subscription-summary-view-${chain_id}`}
      >
        {future ? (
          <div>
            <SubscriptionSummaryRow title={<span>ご利用中のプラン</span>}>
              <div data-value="current-plan" styleName="current-plan">
                {plan_name}
              </div>
            </SubscriptionSummaryRow>
            <SubscriptionSummaryRow title="受講開始日" className="start-date-info">
              <DetailedDate2 date={starts_at} />
            </SubscriptionSummaryRow>
            {hasNextPayment && (
              <SubscriptionSummaryRow title="次回のお支払い" className="next-payment-info">
                <div>
                  <DetailedDate2 date={next_payment_date} />に{next_payment_amount.toLocaleString()}
                  円（税込）
                  <SpBr />
                  を請求予定です。
                </div>
              </SubscriptionSummaryRow>
            )}
          </div>
        ) : (
          <div>
            <SubscriptionSummaryRow title={<span>ご利用中のプラン</span>}>
              <div data-value="current-plan" styleName="current-plan">
                {plan_name}
                {isRecess && (
                  <div>
                    <span styleName="suspension-status">休会中</span>
                  </div>
                )}
                {(isWaitingForPayment || isPaymentCompleted) && (
                  <div>
                    <span styleName="suspension-status">サービス停止中</span>
                  </div>
                )}
                {isSuspended && !isWaitingForPayment && config.planUpdateEnabled && (
                  <div styleName="action-area">
                    {isRecess && (
                      <Button
                        size="small"
                        onClick={() =>
                          GlobalStatesV3.routing.push(V3Routes.subscriptionSuspensionCancelInfo(subscription_code))
                        }
                        fullWidth={isSp}
                      >
                        復帰する
                      </Button>
                    )}
                    {!isRecess && (
                      <Button
                        size="small"
                        onClick={() => onResumeSubscriptionRequest && onResumeSubscriptionRequest(subscription_code)}
                        fullWidth={isSp}
                      >
                        いますぐ復帰する
                      </Button>
                    )}
                  </div>
                )}
                {canChangePlanOrSuspendPlan && (
                  <div styleName="action-area">
                    <Button
                      size="small"
                      className="plan-update-request-button"
                      onClick={() => onUpdateRequest && onUpdateRequest(current_period_membership_item_id)}
                      fullWidth={isSp}
                    >
                      プランを変更する
                    </Button>
                    {/* テキストリンクタイプのボタンはまだshelikes-design-systemにないのでこれまでのButtonコンポーネントを使用する */}
                    <OldButton
                      type="underline"
                      size="s"
                      className="suspension-request-button"
                      styleName="suspension-button"
                      onClick={() =>
                        GlobalStatesV3.routing.push(V3Routes.subscriptionSuspensionInfo(subscription_code))
                      }
                    >
                      休会手続きに進む
                    </OldButton>
                  </div>
                )}
              </div>
              {isPaymentCompleted && (
                <div styleName="plan-warning">
                  <Warning>
                    下記の休会期間中に復帰を行わない場合、自動的に退会処理が行われますのでご注意ください。
                  </Warning>
                </div>
              )}
              {!subscribed && <div styleName="attention">※こちらのプランは休会・退会ができません。</div>}
            </SubscriptionSummaryRow>
            {hasFutureSuspension && (
              <SubscriptionSummaryRow title="休会予約" className="suspension-schedule-info">
                <div>
                  <span styleName="suspension-period">
                    <DetailedDateRange
                      fromDate={subscription_suspension.starts_at}
                      toDate={subscription_suspension.ends_at}
                    />
                  </span>
                  での休会が予約されています
                </div>
                {config.planUpdateEnabled && (
                  <div styleName="cancel-future-suspension">
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() =>
                        onCancelSuspensionRequest &&
                        onCancelSuspensionRequest(
                          subscription_code,
                          subscription_suspension.subscription_suspension_code
                        )
                      }
                      fullWidth={isSp}
                    >
                      休会予約を取り消す
                    </Button>
                  </div>
                )}
              </SubscriptionSummaryRow>
            )}
            <SubscriptionSummaryRow title="現プランの受講可能期間" className="subscription-period-info">
              {strict_expires_at && current_period_starts_at && (
                <DetailedPeriod startsAt={current_period_starts_at} expiresAt={strict_expires_at} />
              )}
              {!strict_expires_at && <DetailedDate2 date={current_period_starts_at} />}
            </SubscriptionSummaryRow>
            {isSuspended && (
              <SubscriptionSummaryRow title="休会期間" className="suspension-info">
                <DetailedPeriod
                  startsAt={subscription_suspension.starts_at}
                  expiresAt={subscription_suspension.ends_at}
                />
              </SubscriptionSummaryRow>
            )}
            {isWaitingForPayment && (
              <SubscriptionSummaryRow title="次回のお支払い" className="next-payment-info">
                <div>
                  <div>未払いにつき現在支払い停止中です。</div>
                  <div styleName="button-wrapper">
                    <Button
                      variant="danger"
                      size="small"
                      onClick={() => GlobalStatesV3.routing.push(V3Routes.payments())}
                      fullWidth={isSp}
                    >
                      再支払いする
                    </Button>
                  </div>
                  <div styleName="button-wrapper">
                    <a href="https://sheinc.notion.site/7b954d53f71946d180cad64a6613d27c">
                      支払いについての詳細を確認する
                    </a>
                  </div>
                </div>
              </SubscriptionSummaryRow>
            )}
            {hasNextPayment && !isWaitingForPayment && (
              <SubscriptionSummaryRow title="次回のお支払い" className="next-payment-info">
                <div>
                  <DetailedDate2 date={next_payment_date} />に{next_payment_amount.toLocaleString()}
                  円（税込）
                  <SpBr />
                  を請求予定です。
                  <br />
                  <span styleName="red">※プランは自動更新となります</span>
                </div>
              </SubscriptionSummaryRow>
            )}
            {hasFutureChangePlan && (
              <SubscriptionSummaryRow title="プラン変更予約">
                <div styleName="update-schedule">
                  <div>
                    <b>
                      <DetailedDate2 date={next_period_starts_at} />
                    </b>
                    に<b>{next_period_membership_item.name}</b>
                    <MobBr />
                    への変更が予約されています。
                  </div>
                  <div id="cancel-plan-change-button" styleName="button-wrapper">
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() =>
                        onUpdateCancelRequest &&
                        onUpdateCancelRequest(current_period_membership_item_id, next_period_membership_item_id)
                      }
                      fullWidth={isSp}
                    >
                      変更を取り消す
                    </Button>
                  </div>
                </div>
              </SubscriptionSummaryRow>
            )}
          </div>
        )}
      </div>
      {(isWaitingForPayment || isPaymentCompleted) && (
        <div styleName="plan-request-warning">
          <Alert>
            <AlertContent
              description={
                <>
                  未納の状態になる前にプラン変更を申し込んでいた場合、一度変更申請がキャンセルとなっております。
                  未納解消後に現在のプラン以外での受講再開をご希望の場合は、
                  <a href="https://line.me/R/ti/p/%40xzy6836c" target="_blank">
                    SHElikes公式LINEまでご連絡
                  </a>
                  をお願いいたします。
                </>
              }
            />
          </Alert>
        </div>
      )}
    </>
  );
};
